/**
 * 接続先の検証。
 *
 * ADM-19。`REACT_APP_API_URL` が未設定のままビルドすると、
 * `undefined` を基底URLとして相対パスへ要求が飛び、画面は「通信できない」
 * としか見えない。設定の誤りは、その場で分かる形にする。
 */

export type ApiUrlProblem =
  | { kind: 'missing' }
  | { kind: 'invalid'; value: string }
  | { kind: 'insecure'; value: string };

/** 問題があればその内容、無ければ null。 */
export function inspectApiBaseUrl(
  value: string | undefined,
  { allowInsecure }: { allowInsecure: boolean }
): ApiUrlProblem | null {
  if (!value) return { kind: 'missing' };

  let url: URL;
  try {
    url = new URL(value);
  } catch {
    return { kind: 'invalid', value };
  }
  if (url.protocol !== 'https:' && url.protocol !== 'http:') {
    return { kind: 'invalid', value };
  }
  if (url.protocol === 'http:' && !allowInsecure) {
    return { kind: 'insecure', value };
  }
  return null;
}

export function describeApiUrlProblem(problem: ApiUrlProblem): string {
  switch (problem.kind) {
    case 'missing':
      return 'REACT_APP_API_URL が設定されていません。環境ごとの .env を確認してください。';
    case 'invalid':
      return `REACT_APP_API_URL の形式が正しくありません: ${problem.value}`;
    case 'insecure':
      return `REACT_APP_API_URL に http を指定できるのは開発環境だけです: ${problem.value}`;
  }
}

/**
 * 起動時に接続先を確認する。
 *
 * developmentではlocalhostのhttpを許す。それ以外の環境で問題があれば
 * 例外にして、誤った接続先のまま配布物が動き続けないようにする。
 */
export function assertApiBaseUrl(
  value: string | undefined,
  environment: string | undefined
): string {
  const allowInsecure = environment !== 'production';
  const problem = inspectApiBaseUrl(value, { allowInsecure });
  if (problem) {
    const message = describeApiUrlProblem(problem);
    if (environment === 'production') throw new Error(message);
    // eslint-disable-next-line no-console
    console.error(message);
    return value ?? '';
  }
  return value as string;
}
